import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import AllInboxRoundedIcon from '@mui/icons-material/AllInboxRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';

import api from '../../utils/functions/api';
import ExpensesTabAll from './ExpensesTabAll';
import ExpensesTabArchive from './ExpensesTabArchive';
import ExpensesTabRejected from './ExpensesTabRejected';

// แท็บ "ทั้งหมด" รวม Success / ปฏิเสธ-ยกเลิก เข้ามาเป็น sub-tabs (เดิมเป็นแท็บแม่แยกกัน) — ตัว component
// ทั้ง 3 ตัวไม่ได้แก้ ใช้ต่อได้ตรง ๆ (props เหมือนกันหมด: onDoubleClickProject/refreshTick) แค่ครอบ
// ด้วยแถบ sub-tab ที่หน้าตาเหมือนแท็บแม่ใน ExpensesList.js
const SUBTABS = [
  {
    key: 'archive',
    label: 'Success',
    icon: CheckCircleRoundedIcon,
    gradient: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
    Component: ExpensesTabArchive,
  },
  {
    key: 'rejected',
    label: 'ปฏิเสธ/ยกเลิก',
    icon: CancelRoundedIcon,
    gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    Component: ExpensesTabRejected,
  },
  {
    key: 'all',
    label: 'ทั้งหมด',
    icon: AllInboxRoundedIcon,
    gradient: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
    Component: ExpensesTabAll,
  },
];

export default function ExpensesTabAllGroup({ onDoubleClickProject, refreshTick = 0 }) {
  const [subTab, setSubTab] = useState('archive');
  const active = SUBTABS.find((t) => t.key === subTab) || SUBTABS[0];
  const ActiveComponent = active.Component;

  // จำนวนรายการต่อ sub-tab — ยิง size=1 เอาแค่ total (pattern เดียวกับแท็บแม่ใน ExpensesList.js)
  const [subCounts, setSubCounts] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        SUBTABS.map(async (t) => {
          try {
            const { data } = await api.get(
              `${process.env.REACT_APP_API_URL}/expenses?tab=${t.key}&size=1&page=1`,
            );
            return [t.key, data?.total ?? 0];
          } catch (error) {
            console.error(error);
            return [t.key, null];
          }
        }),
      );
      if (!cancelled) setSubCounts(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 0.75,
            p: 0.75,
            borderRadius: '18px',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0,0,0,0.35)'
                : '0 4px 20px rgba(15,23,42,0.06)',
          }}
        >
          {SUBTABS.map((t) => {
            const selected = subTab === t.key;
            const Icon = t.icon;
            return (
              <Box
                key={t.key}
                onClick={() => setSubTab(t.key)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.9,
                  px: 2,
                  py: 1,
                  borderRadius: '14px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                  transition:
                    'transform 0.2s ease, background-color 0.2s ease, box-shadow 0.25s ease',
                  transform: selected ? 'translateY(-1px)' : 'none',
                  background: selected ? t.gradient : 'transparent',
                  boxShadow: selected
                    ? '0 6px 16px -4px rgba(0,0,0,0.35)'
                    : 'none',
                  '&:hover': {
                    background: selected
                      ? t.gradient
                      : (theme) =>
                          theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.06)'
                            : 'rgba(15,23,42,0.04)',
                  },
                }}
              >
                <Icon
                  sx={{
                    fontSize: 19,
                    color: selected ? '#fff' : 'text.secondary',
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: selected ? 700 : 500,
                    color: selected ? '#fff' : 'text.secondary',
                  }}
                >
                  {t.label}
                </Typography>
                {typeof subCounts[t.key] === 'number' && (
                  <Box
                    sx={{
                      minWidth: 20,
                      height: 20,
                      px: 0.7,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '999px',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      lineHeight: 1,
                      bgcolor: selected
                        ? 'rgba(255,255,255,0.28)'
                        : (theme) =>
                            theme.palette.mode === 'dark'
                              ? 'rgba(255,255,255,0.12)'
                              : 'rgba(15,23,42,0.07)',
                      color: selected ? '#fff' : 'text.secondary',
                    }}
                  >
                    {subCounts[t.key]}
                  </Box>
                )}
              </Box>
            );
          })}
        </Paper>
      </Box>

      {/* key={active.key} — ให้ state/ตัวกรองของแต่ละ sub-tab เริ่มใหม่เมื่อสลับ ไม่ค้างข้ามกัน */}
      <ActiveComponent
        key={active.key}
        onDoubleClickProject={onDoubleClickProject}
        refreshTick={refreshTick}
      />
    </Box>
  );
}
